import { useMemo, useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash2, Plus } from "lucide-react";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { SCALES, calcGpa, type Scale, type Weight } from "./gpa";
import { NoticeBox } from "@/components/NoticeBox";

interface Course {
  id: number;
  name: string;
  credits: string;
  grade: string;
  weight: Weight;
}

const TOOL_INFO_CONTENT = {
  what: `Calculates your grade point average from a list of courses, their credit hours, and their letter grades, on the US 4.0 scale, the US 4.3 scale (A+ = 4.3) or India's 10-point CGPA scale. It can weight Honors and AP courses and add a previous GPA to give your cumulative GPA.`,
  how: `Pick your grading scale, add each course with its credit hours and grade, and your GPA updates as you edit. Use the bin button on a row to remove a course.`,
  tip: `Some schools use a slightly different scale for plus/minus grades (an A- might be 3.7 or 3.67 depending on the institution) — check your school's specific scale if you need an exact match to your official transcript.`,
};

export default function GpaCalculator() {
  const [courses, setCourses] = useState<Course[]>([
    { id: 1, name: "Course 1", credits: "3", grade: "A", weight: "regular" },
    { id: 2, name: "Course 2", credits: "3", grade: "B+", weight: "regular" },
  ]);
  const [nextId, setNextId] = useState(3);
  const [scale, setScale] = useState<Scale>("us40");
  const [weighted, setWeighted] = useState(false);
  const [useCum, setUseCum] = useState(false);
  const [prevGpa, setPrevGpa] = useState("");
  const [prevCredits, setPrevCredits] = useState("");

  const changeScale = (v: Scale) => {
    setScale(v);
    const g = SCALES[v].grades;
    setCourses((cs) => cs.map((c) => (g[c.grade] === undefined ? { ...c, grade: Object.keys(g)[Object.keys(g).length > 2 ? 1 : 0] } : c)));
  };

  const addCourse = () => {
    setCourses([...courses, { id: nextId, name: `Course ${nextId}`, credits: "3", grade: Object.keys(SCALES[scale].grades)[1], weight: "regular" }]);
    setNextId(nextId + 1);
  };

  const removeCourse = (id: number) => {
    setCourses(courses.filter(c => c.id !== id));
  };

  const updateCourse = (id: number, field: keyof Course, value: string) => {
    setCourses(courses.map(c => (c.id === id ? { ...c, [field]: value } : c)));
  };

  const pg = Number(prevGpa.replace(",", ".")), pc = Number(prevCredits.replace(",", "."));
  const prior = useCum && prevGpa !== "" && prevCredits !== "" && Number.isFinite(pg) && Number.isFinite(pc) && pc > 0 && pg >= 0 && pg <= SCALES[scale].max + 1 ? { gpa: pg, credits: pc } : null;
  const result = useMemo(() => calcGpa(courses, scale, weighted, prior), [courses, scale, weighted, prior?.gpa, prior?.credits]);
  const excluded = Object.keys(result.errors).length;
  const decimals = scale === "india10" ? 2 : 2;

  const { blocked } = useLogToolUsage(true, 3000);


  return (
    <ToolContainer>
      <div className="flex gap-4 flex-wrap items-center mb-4 text-sm">
        <label className="flex items-center gap-2">Grading scale
          <select className="border rounded px-2 py-1 bg-background" value={scale} onChange={(e) => changeScale(e.target.value as Scale)} aria-label="Grading scale">
            {(Object.keys(SCALES) as Scale[]).map((k) => <option key={k} value={k}>{SCALES[k].label}</option>)}
          </select>
        </label>
        {scale !== "india10" && <label className="flex items-center gap-2"><input type="checkbox" aria-label="Weighted GPA" checked={weighted} onChange={(e) => setWeighted(e.target.checked)} /> Weighted (Honors +0.5, AP +1.0)</label>}
      </div>
      <div className="space-y-3">
        {courses.map((course, i) => (
          <div key={course.id}>
            <div className="flex items-center gap-2 flex-wrap">
              <Input className="flex-1 min-w-[120px]" value={course.name} onChange={e => updateCourse(course.id, "name", e.target.value)} placeholder="Course name" aria-label={`Course ${i + 1} name`} />
              <Input className="w-20" inputMode="decimal" value={course.credits} onChange={e => updateCourse(course.id, "credits", e.target.value)} placeholder="Credits" aria-label={`Course ${i + 1} credits`} aria-invalid={!!result.errors[i]} />
              <Select value={course.grade} onValueChange={v => updateCourse(course.id, "grade", v)}>
                <SelectTrigger className="w-24" aria-label={`Course ${i + 1} grade`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.keys(SCALES[scale].grades).map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
              {weighted && scale !== "india10" && (
                <select className="border rounded px-2 py-2 bg-background text-sm" value={course.weight} onChange={(e) => updateCourse(course.id, "weight", e.target.value)} aria-label={`Course ${i + 1} level`}>
                  <option value="regular">Regular</option><option value="honors">Honors</option><option value="ap">AP / IB</option>
                </select>
              )}
              <Button size="icon" variant="ghost" onClick={() => removeCourse(course.id)} aria-label={`Remove course ${i + 1}`} title="Remove this course">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            {result.errors[i] && <p className="text-xs text-destructive mt-1 ml-1" data-testid="gpa-error">{result.errors[i]}</p>}
          </div>
        ))}
      </div>

      <Button variant="outline" className="mt-4" onClick={addCourse} aria-label="Add a course">
        <Plus className="h-4 w-4 mr-2" /> Add Course
      </Button>

      <div className="mt-4 text-sm space-y-2">
        <label className="flex items-center gap-2"><input type="checkbox" checked={useCum} onChange={(e) => setUseCum(e.target.checked)} /> Include my previous GPA (cumulative)</label>
        {useCum && (
          <div className="flex gap-2 flex-wrap">
            <Input className="w-36" inputMode="decimal" placeholder="Previous GPA" aria-label="Previous GPA" value={prevGpa} onChange={(e) => setPrevGpa(e.target.value)} />
            <Input className="w-40" inputMode="decimal" placeholder="Previous credits" aria-label="Previous credits" value={prevCredits} onChange={(e) => setPrevCredits(e.target.value)} />
          </div>
        )}
      </div>

      <Card className="mt-6 p-6 text-center">
        <p className="text-sm text-muted-foreground mb-1">{scale === "india10" ? "Your CGPA" : useCum ? "Your cumulative GPA" : "Your GPA"}</p>
        <p className="text-4xl font-bold text-primary" data-testid="gpa-value">{blocked ? "—" : result.gpa === null ? "—" : result.gpa.toFixed(decimals)}</p>
        {excluded > 0 && !blocked && (
          <NoticeBox role="alert" className="text-xs mt-2" testId="gpa-excluded">
            {excluded === 1 ? "1 course has" : `${excluded} courses have`} a problem with {excluded === 1 ? "its" : "their"} credits or grade and {excluded === 1 ? "was" : "were"} left out of this result. Fix {excluded === 1 ? "it" : "them"} to include {excluded === 1 ? "it" : "them"}.
          </NoticeBox>
        )}
        {result.gpa === null && !blocked && <p className="text-xs text-muted-foreground mt-1">Add at least one course with credits above 0.</p>}
        {result.gpa !== null && !blocked && (
          <p className="text-xs text-muted-foreground mt-2">
            {result.totalCredits.toLocaleString("en-US", { maximumFractionDigits: 2 })} credits · {result.qualityPoints.toLocaleString("en-US", { maximumFractionDigits: 2 })} quality points
            {result.percent !== undefined && <> · about {result.percent.toFixed(1)}% (CGPA × 9.5, the common university rule)</>}
          </p>
        )}
      </Card>
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
